import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import {
  AiChatQueryParams,
  BannerPayload,
  CreateAccountCodeParams,
  GetBillingParams,
  GetInternalReportParams,
  PayslipVerifierQueryParams,
  SaveReportNoteParams,
  UpdateAccountCodeParams,
  UpdateBannerPayload,
} from '../types';

// Billing
const getBillingAccountCodes = createAsyncThunk(
  'superAdmin/getBillingAccountCodes',
  async (params: GetBillingParams | undefined, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.billing.accountCode, {
        params,
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const createAccountCode = createAsyncThunk(
  'superAdmin/createAccountCode',
  async (params: CreateAccountCodeParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.billing.accountCode,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateAccountCode = createAsyncThunk(
  'superAdmin/updateAccountCode',
  async (params: UpdateAccountCodeParams, { rejectWithValue }) => {
    try {
      const { id, ...body } = params;
      const response = await jwtAxios.put(
        `${endPoints.billing.accountCode}/${id}`,
        body,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const deleteAccountCode = createAsyncThunk(
  'superAdmin/deleteAccountCode',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(
        `${endPoints.billing.accountCode}/${id}`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

// Internal Report
const getInternalReport = createAsyncThunk(
  'superAdmin/getInternalReport',
  async (params: GetInternalReportParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.internalReport.main, {
        params,
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getSmartPensionReport = createAsyncThunk(
  'superAdmin/getSmartPensionReport',
  async (params: GetInternalReportParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.internalReport.smartPension,
        { params },
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const saveReportNote = createAsyncThunk(
  'superAdmin/saveReportNote',
  async (params: SaveReportNoteParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.internalReport.note,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

// Banner
const getBannerList = createAsyncThunk(
  'superAdmin/getBannerList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.banner.main);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const createBanner = createAsyncThunk(
  'superAdmin/createBanner',
  async (params: BannerPayload, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.banner.main, params);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const updateBanner = createAsyncThunk(
  'superAdmin/updateBanner',
  async ({ id, ...params }: UpdateBannerPayload, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(
        `${endPoints.banner.main}/${id}`,
        params,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const deleteBanner = createAsyncThunk(
  'superAdmin/deleteBanner',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(
        `${endPoints.banner.main}/${id}`,
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

// AI Chats (cross-tenant, super-admin). Exported so the CSV export path reuses
// the exact same param serialization → export stays in lockstep with the table.
export const buildAiChatParams = (params: AiChatQueryParams) => ({
  ...params,
  hasError: params.hasError ? 'true' : undefined,
});

const getAiChatSessions = createAsyncThunk(
  'superAdmin/getAiChatSessions',
  async (params: AiChatQueryParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.ai.admin.sessions, {
        params: buildAiChatParams(params),
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getAiChatStats = createAsyncThunk(
  'superAdmin/getAiChatStats',
  async (params: AiChatQueryParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.ai.admin.stats, {
        params: buildAiChatParams(params),
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const deleteAiChatSession = createAsyncThunk(
  'superAdmin/deleteAiChatSession',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(endPoints.ai.admin.session(id));
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

// Payslip Verifier (cross-tenant, super-admin). Same param passthrough as AI
// Chats so a future CSV export can reuse the exact serialization.
export const buildPayslipVerifierParams = (
  params: PayslipVerifierQueryParams,
) => ({ ...params });

const getPayslipVerifications = createAsyncThunk(
  'superAdmin/getPayslipVerifications',
  async (params: PayslipVerifierQueryParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.payslipVerifier.admin.list, {
        params: buildPayslipVerifierParams(params),
      });
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const getPayslipVerifierStats = createAsyncThunk(
  'superAdmin/getPayslipVerifierStats',
  async (params: PayslipVerifierQueryParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.payslipVerifier.admin.stats,
        { params: buildPayslipVerifierParams(params) },
      );
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const superAdminAct = {
  getBillingAccountCodes,
  createAccountCode,
  updateAccountCode,
  deleteAccountCode,
  getInternalReport,
  getSmartPensionReport,
  saveReportNote,
  getBannerList,
  createBanner,
  updateBanner,
  deleteBanner,
  getAiChatSessions,
  getAiChatStats,
  deleteAiChatSession,
  getPayslipVerifications,
  getPayslipVerifierStats,
};
